import { useState, useEffect, useRef, useCallback } from 'react'
import { getSchemas, getUploads, uploadFile, uploadDocumentAdvanced, deleteUpload, getHydrationStatus, getDataHealth, syncConnector, getSyncStatus, repullRaasTable, exportProductTablesReport, getVendors, getProducts, getDocuments, deleteDocument, deleteTable } from '../../api'
import Connectors from '../Connectors/Connectors'
import LoadError from '../../components/LoadError'
import { loadList, settledList, loadErrorMessage } from '../../utils/loadState'
import { rebuildAnalytics } from '../../utils/hydration'

// Routing is by FILE TYPE, not by truth-type category -- a CSV is tabular data no
// matter which of the 3 categories below it's classified under. Spreadsheets go to
// /api/upload -> DuckDB (Tables list). Everything else (PDF, Word, config exports,
// screenshots, ...) goes to /api/documents/upload -> ChromaDB for RAG (Documents
// list). The category only tags truth_type; it never used to pick the pipeline --
// that conflation was itself the bug (item 77).
const TABULAR_EXTENSIONS = new Set(['.csv', '.xlsx', '.xls', '.tsv'])

function isTabularFile(filename) {
  const dot = (filename || '').lastIndexOf('.')
  if (dot < 0) return false
  return TABULAR_EXTENSIONS.has(filename.slice(dot).toLowerCase())
}

const TRUTH_TYPES = [
  {
    id: 'truth_1_actual',
    label: 'Actual Data',
    desc: 'Live employee, payroll, or transaction records',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/>
      </svg>
    ),
  },
  {
    id: 'truth_2_expected',
    label: 'Expected / Requirements',
    desc: 'SOW, design docs, business rules, or specs',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    id: 'truth_5_config',
    label: 'System Configuration',
    desc: 'UKG setup exports, config screenshots, or settings files',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
      </svg>
    ),
  },
]

function formatDate(str) {
  if (!str) return ''
  const d = new Date(str)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function DataManagementTab({ customerId }) {
  const fileInputRef = useRef(null)
  const [tables, setTables] = useState([])
  const [uploads, setUploads] = useState([])
  const [selectedTable, setSelectedTable] = useState(null)
  const [hydrationStatus, setHydrationStatus] = useState(null)
  const [hydrating, setHydrating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [selectedTruth, setSelectedTruth] = useState('truth_1_actual')
  const [uploadMsg, setUploadMsg] = useState(null)
  const [documents, setDocuments] = useState([])
  // A failed documents read is an error with a Retry, never "No documents uploaded yet".
  const [docsError, setDocsError] = useState(null)
  const [vendors, setVendors] = useState([])
  const [products, setProducts] = useState([])
  // A failed vendor/product read is an error with a Retry, never an empty dropdown (item 201).
  const [vendorsError, setVendorsError] = useState(null)
  const [productsError, setProductsError] = useState(null)
  const [selectedVendor, setSelectedVendor] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')
  const [isSdmExport, setIsSdmExport] = useState(false)
  // INGESTION TRUTH. row_count says how MUCH data a table has; it never says how OLD.
  // Staleness is the proven cause of wrong numbers, so age is shown per table and the
  // re-pull that fixes it sits on the same row as the problem.
  const [health, setHealth] = useState(null)
  // A failed read is shown as a failure with a Retry, never as an empty list, a zero, or
  // a table list with no ages and no Re-pull buttons (item 201's pattern).
  const [tablesError, setTablesError] = useState(null)
  const [uploadsError, setUploadsError] = useState(null)
  const [hydrationError, setHydrationError] = useState(null)
  const [healthError, setHealthError] = useState(null)
  const [hydrateError, setHydrateError] = useState(null)
  const [hydrateMsg, setHydrateMsg] = useState(null)
  const [repulling, setRepulling] = useState(null)
  const [repullMsg, setRepullMsg] = useState(null)
  const [exportingReport, setExportingReport] = useState(false)


  // Age, stated plainly. NO COLOUR THRESHOLDS — "stale" depends on the endpoint's own
  // cadence and nobody has ruled what that is per endpoint, so inventing a red/amber
  // line here would be authoring a rule the customer configures. Show the number.
  const healthByTable = {}
  for (const t of (health?.tables || [])) healthByTable[t.table] = t

  const ageLabel = (h) => {
    if (!h) return null
    if (h.source !== 'api') return null
    if (h.never_synced) return 'never synced'
    if (h.age_hours == null) return null
    if (h.age_hours < 1) return 'synced <1h ago'
    if (h.age_hours < 48) return `synced ${Math.round(h.age_hours)}h ago`
    return `synced ${Math.round(h.age_hours / 24)}d ago`
  }

  // The server's detail, not the whole "API 502: {json}" string api.js throws.
  const errText = (e) => {
    const m = String(e?.message || e).match(/^API \d+: (.*)$/s)
    if (!m) return String(e?.message || e)
    try { return JSON.parse(m[1]).detail || m[1] } catch { return m[1] }
  }

  const SYNC_DONE = ['completed', 'completed_with_errors', 'complete', 'error', 'failed', 'interrupted']

  // Re-pull ONE table, the way that table is actually loaded. data-health decides HOW
  // (h.repull): a RaaS table re-executes the report it came from; an API table syncs
  // through the connection that declares its endpoint. This used to send every table
  // to /sync with the FIRST saved connection, so RaaS re-pulls loaded nothing -- and
  // the message saying so was set but never rendered (2026-10-06).
  // One re-pull at a time: each one queues the post-ingest finalize, so clicking down
  // the list would stack overlapping finalize runs.
  const rePull = async (h) => {
    const target = h?.repull
    if (!target || repulling) return
    setRepulling(h.table); setRepullMsg(null)
    try {
      if (target.kind === 'raas') {
        const res = await repullRaasTable(customerId, h.table)
        setRepullMsg({
          kind: 'ok',
          text: `Re-pulled report ${res.report_name}: ${Number(res.row_count).toLocaleString()} rows reloaded. Post-load checks queued.`,
        })
        await loadData()
      } else {
        // A payroll-history table (target.full_history) reloads in full, not incrementally.
        // A child, renamed or derived table (target.via_parent) is re-pulled through the
        // endpoint that writes it -- which rewrites that endpoint's other tables too.
        const what = target.via_parent ? `${target.endpoint} (the parent of ${h.endpoint})` : target.endpoint
        const res = await syncConnector(customerId, target.vendor_id, target.product_id, [target.endpoint], {}, !!target.full_history)
        if (res?.status === 'already_running') {
          setRepullMsg({ kind: 'error', text: `Re-pull of ${what} not started: a sync that writes these tables is already running (job ${res.job_id}). Try again when it finishes.` })
          return
        }
        setRepullMsg({ kind: 'ok', text: `Re-pulling ${what}…` })
        // The job is ASYNCHRONOUS -- wait for it, and report what it actually did.
        let st = null
        for (let i = 0; i < 400; i++) {
          await new Promise(r => setTimeout(r, 3000))
          st = await getSyncStatus(res.job_id)
          if (SYNC_DONE.includes(st.status)) break
        }
        // Nothing written means the post-load steps were skipped -- say so, with the reason.
        const skipped = st?.post_sync && st.post_sync.ran === false ? ` ${st.post_sync.reason}.` : ''
        if (!st || !SYNC_DONE.includes(st.status)) {
          setRepullMsg({ kind: 'error', text: `Re-pull of ${what} is still running (job ${res.job_id}). Refresh later to see the new sync time.` })
        } else if ((st.errors || []).length || st.status === 'error' || st.status === 'failed') {
          setRepullMsg({ kind: 'error', text: `Re-pull of ${what} finished with errors: ${(st.errors || []).slice(0, 3).join('; ') || st.status}.${skipped}` })
        } else {
          setRepullMsg({ kind: st.post_sync?.ran === false ? 'error' : 'ok', text: `Re-pulled ${what}: ${Number(st.total_rows || 0).toLocaleString()} rows.${skipped}` })
        }
        await loadData()
      }
    } catch (e) {
      setRepullMsg({ kind: 'error', text: `Re-pull failed for ${h.table}: ${errText(e)}` })
    } finally {
      setRepulling(null)
    }
  }

  const handleExportProductTables = async (productUid) => {
    setExportingReport(true)
    try {
      const blob = await exportProductTablesReport(customerId, productUid)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${customerId}_${productUid}_tables.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Report export failed:', err)
      alert('Could not build the report: ' + err.message)
    } finally {
      setExportingReport(false)
    }
  }

  const loadData = useCallback(async () => {
    try {
      const [sch, up, hyd, hlt, docs] = await Promise.allSettled([
        getSchemas(customerId),
        getUploads(customerId),
        getHydrationStatus(customerId),
        getDataHealth(customerId),
        getDocuments(customerId),
      ])
      // A body that carries `error` is a failed read too, not "no tables" (GET
      // /api/admin/schemas answers 503 when its read fails; the check stays for any 200
      // that reports one).
      const bodyError = (r) => (r.status === 'fulfilled' && r.value?.error
        ? { status: 'rejected', reason: new Error(String(r.value.error)) } : r)
      const tl = settledList(bodyError(sch), 'tables')
      setTables(tl.items || [])
      setTablesError(tl.error)
      const ul = settledList(bodyError(up), 'uploads')
      setUploads(ul.items || [])
      setUploadsError(ul.error)
      setHydrationStatus(hyd.status === 'fulfilled' ? hyd.value : null)
      setHydrationError(hyd.status === 'fulfilled' ? null : loadErrorMessage(hyd.reason))
      // A failed health call leaves health null, which renders NOTHING rather than a
      // reassuring green -- and says so. An unknown age must not look like a fresh one.
      setHealth(hlt.status === 'fulfilled' ? hlt.value : null)
      setHealthError(hlt.status === 'fulfilled' ? null : loadErrorMessage(hlt.reason))
      const dl = settledList(docs, 'documents')
      setDocuments(dl.items || [])
      setDocsError(dl.error)
    } catch (err) {
      console.error('Load failed:', err)
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => { loadData() }, [loadData])

  async function loadVendors() {
    const { items, error } = await loadList(getVendors, 'vendors')
    setVendors(items || [])
    setVendorsError(error)
  }

  async function loadProducts(vendor) {
    const { items, error } = await loadList(() => getProducts(vendor), 'products')
    setProducts(items || [])
    setProductsError(error)
  }

  useEffect(() => { loadVendors() }, [])

  useEffect(() => {
    setProductsError(null)
    if (!selectedVendor) { setProducts([]); setSelectedProduct(''); return }
    loadProducts(selectedVendor)
    setSelectedProduct('')
  }, [selectedVendor])

  async function handleUpload(files) {
    if (!files || files.length === 0) return
    setUploading(true)
    setUploadMsg(null)
    const ok = { tabular: 0, document: 0 }
    const failed = []
    try {
      for (const file of files) {
        const tabular = isTabularFile(file.name)
        try {
          // Both endpoints return HTTP 200 even when the write itself failed
          // (the failure is carried in the body's success/error fields) --
          // a thrown exception here is a transport/auth failure, not the only
          // way this can fail, so result.success still has to be checked.
          const result = tabular
            ? await uploadFile(file, customerId, selectedTruth, selectedVendor || undefined, selectedProduct || undefined, isSdmExport)
            : await uploadDocumentAdvanced(file, customerId, selectedTruth, selectedVendor || undefined, selectedProduct || undefined)
          if (result?.success === false) {
            const reason = result.error || (result.errors && result.errors.join('; ')) || 'unknown reason'
            failed.push(`${file.name}: ${reason}`)
          } else {
            ok[tabular ? 'tabular' : 'document']++
          }
        } catch (err) {
          failed.push(`${file.name}: ${err.message || err}`)
        }
      }
      await loadData()
    } finally {
      setUploading(false)
      if (failed.length) {
        setUploadMsg({ kind: 'error', text: failed.join(' | ') })
      } else {
        const parts = []
        if (ok.tabular) parts.push(`${ok.tabular} file${ok.tabular > 1 ? 's' : ''} into Tables`)
        if (ok.document) parts.push(`${ok.document} document${ok.document > 1 ? 's' : ''} into Documents (below)`)
        if (parts.length) setUploadMsg({ kind: 'success', text: `Uploaded ${parts.join(' and ')}.` })
      }
    }
  }

  // Rebuild Analytics: start the rebuild, wait on ITS job, show what it did. A rebuild
  // that could not start or did not finish cleanly is shown with LoadError, never
  // swallowed (it used to be console.error only -- and polled forever on a failure).
  async function handleHydrate() {
    setHydrating(true)
    setHydrateError(null)
    setHydrateMsg(null)
    try {
      const { message } = await rebuildAnalytics(customerId, (s) => { setHydrationStatus(s); setHydrationError(null) })
      setHydrateMsg(message)
    } catch (err) {
      setHydrateError(loadErrorMessage(err))
    } finally {
      setHydrating(false)
      await loadData()
    }
  }

  async function handleDeleteUpload(uploadId) {
    if (!confirm('Delete this upload?')) return
    try {
      await deleteUpload(uploadId)
      await loadData()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  async function handleClearAllUploads() {
    if (!confirm(`Delete all ${uploads.length} uploads? This cannot be undone.`)) return
    try {
      for (const u of uploads) {
        await deleteUpload(u.upload_id || u.id)
      }
      await loadData()
    } catch (err) {
      console.error('Clear all failed:', err)
    }
  }

  async function handleDeleteDocument(fileName) {
    if (!confirm(`Delete "${fileName}"? This cannot be undone.`)) return
    try {
      await deleteDocument(customerId, fileName)
      await loadData()
    } catch (err) {
      console.error('Delete failed:', err)
      alert('Delete failed: ' + (err.message || err))
    }
  }

  async function handleDeleteTable(tableName) {
    if (!confirm(`Delete table "${tableName}"? This removes it from DuckDB and every tracking table. Its findings are kept; open ones are dismissed as "table removed". This cannot be undone.`)) return
    try {
      await deleteTable(customerId, tableName)
      setSelectedTable(null)
      await loadData()
    } catch (err) {
      console.error('Delete table failed:', err)
      alert('Delete failed: ' + (err.message || err))
    }
  }

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleUpload(e.dataTransfer.files)
  }

  const totalRows = tables.reduce((sum, t) => sum + (t.row_count || 0), 0)

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--gray-400)' }}>
      <div className="dm-spinner" /> Loading data…
    </div>
  )

  return (
    <div className="dm-page" style={{ paddingTop: 0 }}>
      <Connectors customerId={customerId} />
      <div className="dm-grid">
        {/* Left: Upload + Uploads List */}
        <div className="dm-col">

          {/* Truth Type Selector */}
          <div className="dm-card" style={{ marginBottom: 12 }}>
            <div className="dm-card-header" style={{ marginBottom: 10 }}>
              <h3 className="dm-card-title">Classify Upload</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {TRUTH_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTruth(t.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 7, cursor: 'pointer',
                    border: selectedTruth === t.id ? '1.5px solid var(--green)' : '1.5px solid var(--gray-200)',
                    background: selectedTruth === t.id ? 'var(--green-light)' : 'var(--white)',
                    textAlign: 'left', fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  <span style={{ color: selectedTruth === t.id ? 'var(--green-dark)' : 'var(--gray-400)', flexShrink: 0 }}>
                    {t.icon}
                  </span>
                  <span>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: selectedTruth === t.id ? 'var(--green-dark)' : 'var(--gray-700)' }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-400)', marginTop: 1 }}>{t.desc}</div>
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <select
                value={selectedVendor}
                onChange={e => setSelectedVendor(e.target.value)}
                style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1.5px solid var(--gray-200)', fontFamily: 'inherit' }}
              >
                <option value="">Vendor (optional)</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <select
                value={selectedProduct}
                onChange={e => setSelectedProduct(e.target.value)}
                disabled={!selectedVendor}
                style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1.5px solid var(--gray-200)', fontFamily: 'inherit' }}
              >
                <option value="">Product (optional)</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {vendorsError && <LoadError inline what="vendors" error={vendorsError} onRetry={loadVendors} />}
            {productsError && <LoadError inline what="products" error={productsError} onRetry={() => loadProducts(selectedVendor)} />}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: 'var(--gray-700)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isSdmExport}
                onChange={e => setIsSdmExport(e.target.checked)}
              />
              This is a UKG SDM (Setup Data Manager) bulk export
            </label>
          </div>

          {/* Upload Zone */}
          <div
            className={`dm-upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ cursor: uploading ? 'default' : 'pointer' }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files)}
            />
            {uploading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%' }}>
                <div className="dm-spinner" />
                <p className="dm-upload-text">Uploading…</p>
              </div>
            ) : (
              <>
                <div className="dm-upload-icon">📁</div>
                <p className="dm-upload-text">
                  Drop files here or <strong>click to browse</strong>
                </p>
                <p className="dm-upload-hint">
                  CSV, Excel, or TSV loads as a table below. Anything else (PDF, Word,
                  PowerPoint, HTML, email, text, and more) uploads as a document for search.
                </p>
              </>
            )}
          </div>

          {uploadMsg && (
            <div
              style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 7, fontSize: 12.5,
                border: `1.5px solid ${uploadMsg.kind === 'error' ? 'var(--red)' : 'var(--green)'}`,
                background: uploadMsg.kind === 'error' ? 'var(--red-light, #fdecea)' : 'var(--green-light)',
                color: uploadMsg.kind === 'error' ? 'var(--red)' : 'var(--green-dark)',
              }}
            >
              {uploadMsg.text}
            </div>
          )}

          {/* Hydration Controls */}
          <div className="dm-card">
            <div className="dm-card-header">
              <h3 className="dm-card-title">Hydration</h3>
              <span className={`dm-badge ${hydrationStatus?.needs_hydration ? 'pending' : 'ready'}`}>
                {hydrating ? 'Running…' : hydrationError ? 'Unknown' : hydrationStatus?.needs_hydration ? 'Needs Rebuild' : 'Current'}
              </span>
            </div>
            {hydrationError && <LoadError inline what="hydration status" error={hydrationError} onRetry={loadData} />}
            <div className="dm-hydration-info">
              <p>{hydrationError ? '—' : (hydrationStatus?.duckdb_tables?.length || 0)} tables in analytics DB</p>
              {hydrationStatus?.needs_hydration && hydrationStatus?.supabase_uploads > 0 && (
                <p className="dm-hydration-pending">{hydrationStatus.supabase_uploads} uploads pending hydration</p>
              )}
            </div>
            <button
              className="dm-hydrate-btn"
              onClick={handleHydrate}
              disabled={hydrating}
            >
              {hydrating ? 'Rebuilding Analytics…' : '🔄 Rebuild Analytics'}
            </button>
            {hydrateError && <LoadError inline what="the rebuilt tables" error={hydrateError} onRetry={handleHydrate} />}
            {hydrateMsg && !hydrateError && <p className="dm-hydration-done" role="status">{hydrateMsg}</p>}
          </div>

          {/* Uploads List */}
          <div className="dm-card">
            <div className="dm-card-header">
              <h3 className="dm-card-title">Uploads</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="dm-card-count">{uploadsError ? '—' : uploads.length}</span>
                {uploads.length > 0 && (
                  <button
                    onClick={handleClearAllUploads}
                    style={{
                      fontSize: 11, fontWeight: 600, color: 'var(--red)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit', padding: '0 2px',
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {uploadsError ? (
              <LoadError what="uploads" error={uploadsError} onRetry={loadData} />
            ) : uploads.length === 0 ? (
              <p className="dm-empty">No files uploaded yet</p>
            ) : (
              <div className="dm-upload-list">
                {uploads.map((u, i) => (
                  <div key={i} className="dm-upload-item">
                    <div className="dm-upload-file-icon">
                      {(u.file_name || '').endsWith('.csv') ? 'CSV' : 'XLS'}
                    </div>
                    <div className="dm-upload-info">
                      <div className="dm-upload-name">{u.file_name}</div>
                      <div className="dm-upload-meta">
                        {u.row_count ? `${u.row_count.toLocaleString()} rows` : ''} · {u.created_at?.split('T')[0] || ''}
                      </div>
                    </div>
                    <span className={`dm-upload-status ${u.status || 'ready'}`}>{u.status || 'ready'}</span>
                    <button className="dm-delete-btn" onClick={() => handleDeleteUpload(u.upload_id || u.id)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents List (non-tabular uploads -- ChromaDB/RAG, not the DuckDB Tables list) */}
          <div className="dm-card">
            <div className="dm-card-header">
              <h3 className="dm-card-title">Documents</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="dm-card-count">{docsError ? '—' : documents.length}</span>
                {documents.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Delete all ${documents.length} documents? This cannot be undone.`)) return
                      for (const d of documents) await deleteDocument(customerId, d.file_name)
                      await loadData()
                    }}
                    style={{
                      fontSize: 11, fontWeight: 600, color: 'var(--red)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit', padding: '0 2px',
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {docsError ? (
              <LoadError what="documents" error={docsError} onRetry={loadData} />
            ) : documents.length === 0 ? (
              <p className="dm-empty">No documents uploaded yet</p>
            ) : (
              <div className="dm-upload-list">
                {documents.map((d, i) => (
                  <div key={i} className="dm-upload-item">
                    <div className="dm-upload-file-icon">DOC</div>
                    <div className="dm-upload-info">
                      <div className="dm-upload-name">{d.file_name}</div>
                      <div className="dm-upload-meta">
                        {d.truth_type ? d.truth_type.replace(/^truth_\d_/, '') : ''}
                        {d.total_chunks ? ` · ${d.total_chunks} chunk${d.total_chunks > 1 ? 's' : ''}` : ''}
                        {d.ingested_at && d.ingested_at !== 'unknown' ? ` · ${d.ingested_at.split('T')[0]}` : ''}
                      </div>
                    </div>
                    <button className="dm-delete-btn" onClick={() => handleDeleteDocument(d.file_name)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Table Browser */}
        <div className="dm-col">
          <div className="dm-card dm-tables-card">
            <div className="dm-card-header">
              <span>
                <h3 className="dm-card-title" style={{ display: 'inline' }}>Tables</h3>{' '}
                <span className="dm-card-count">{tablesError ? '—' : `${tables.length} tables · ${totalRows.toLocaleString()} rows`}</span>
              </span>
              <button
                className="dm-export-report-btn"
                disabled={exportingReport}
                onClick={() => handleExportProductTables('ukg_pro_wfm')}
                title="Download a spreadsheet of every WFM table populated for this customer, with row/column counts and real column lists"
              >
                {exportingReport ? 'Building…' : 'Download WFM tables (.xlsx)'}
              </button>
            </div>
            {repullMsg && (
              <div
                role="status"
                style={{
                  margin: '0 0 10px', padding: '8px 12px', borderRadius: 7, fontSize: 12.5,
                  border: `1.5px solid ${repullMsg.kind === 'error' ? 'var(--red)' : 'var(--green)'}`,
                  background: repullMsg.kind === 'error' ? 'var(--red-light, #fdecea)' : 'var(--green-light)',
                  color: repullMsg.kind === 'error' ? 'var(--red)' : 'var(--green-dark)',
                }}
              >
                {repullMsg.text}
              </div>
            )}
            {healthError && !tablesError && (
              <LoadError inline what="sync ages and re-pull options" error={healthError} onRetry={loadData} />
            )}
            {tablesError ? (
              <LoadError what="tables" error={tablesError} onRetry={loadData} />
            ) : tables.length === 0 ? (
              <p className="dm-empty">No tables loaded. Upload data and run hydration.</p>
            ) : (
              <div className="dm-table-browser">
                <div className="dm-table-list">
                  {tables.map((t, i) => {
                    const rawName = t.table_name || t.name || ''
                    const isApi = rawName.includes('_api_')
                    const name = rawName.replace(`cust_${customerId}_api_`, '').replace(`cust_${customerId}_`, '')
                    const sourceFile = t.source_file || ''
                    const loadedAt = formatDate(t.updated_at || t.created_at)
                    const rowCount = t.row_count != null ? t.row_count.toLocaleString() : '—'
                    const colCount = t.column_count || 0
                    return (
                      <div key={i} className="dm-table-row-wrap">
                      <button
                        className={`dm-table-row ${selectedTable === i ? 'selected' : ''}`}
                        onClick={() => setSelectedTable(selectedTable === i ? null : i)}
                      >
                        <span className="dm-table-icon">📋</span>
                        <span className="dm-table-name-wrap">
                          <span className="dm-table-name-row">
                            <span className="dm-table-name">{name}</span>
                            <span className={`dm-table-source-badge ${isApi ? 'api' : 'upload'}`}>
                              {isApi ? 'API' : 'Upload'}
                            </span>
                            {!isApi && (
                              <span className="dm-table-warning" title="Not yet visible to metric engine — _api_ prefix required">⚠</span>
                            )}
                          </span>
                          <span className="dm-table-counts">
                            {rowCount} rows · {colCount} cols{loadedAt ? ` · Loaded ${loadedAt}` : ''}
                            {ageLabel(healthByTable[rawName]) && (
                              <> · <span className="dm-table-age">{ageLabel(healthByTable[rawName])}</span></>
                            )}
                            {healthByTable[rawName]?.repull_none_reason && (
                              <> · <span className="dm-table-norepull" title={healthByTable[rawName].repull_none_reason}>no re-pull: {healthByTable[rawName].repull_none_reason}</span></>
                            )}
                            {healthByTable[rawName]?.registered === false && (
                              <> · <span className="dm-table-unreg" title="Loaded, but not in the knowledge catalog — nothing that reads the catalog can find it">unregistered</span></>
                            )}
                          </span>
                          {sourceFile && (
                            <span className="dm-table-meta2">{sourceFile}</span>
                          )}
                        </span>
                      </button>
                      {healthByTable[rawName]?.repull && (
                        <button
                          className="dm-repull-btn"
                          disabled={!!repulling}
                          title={healthByTable[rawName].repull.kind === 'raas'
                            ? `Re-run report ${healthByTable[rawName].repull.report_name} and reload this table`
                            : healthByTable[rawName].repull.via_parent
                              ? `Re-pull ${healthByTable[rawName].repull.endpoint} from the vendor: the endpoint that writes this table (and its other tables)`
                              : `Re-pull ${healthByTable[rawName].repull.endpoint} from the vendor`}
                          onClick={(e) => { e.stopPropagation(); rePull(healthByTable[rawName]) }}
                        >
                          {repulling === rawName ? '…'
                            : healthByTable[rawName].repull.via_parent ? `Re-pull ${healthByTable[rawName].repull.endpoint}` : 'Re-pull'}
                        </button>
                      )}
                      <button
                        className="dm-delete-btn"
                        title={`Delete table ${name}`}
                        onClick={(e) => { e.stopPropagation(); handleDeleteTable(rawName) }}
                      >
                        ×
                      </button>
                      </div>
                    )
                  })}
                </div>

                {selectedTable !== null && tables[selectedTable] && (
                  <div className="dm-column-detail">
                    <div className="dm-column-header">
                      <h4>{(tables[selectedTable].table_name || tables[selectedTable].name || '').replace(`cust_${customerId}_`, '')}</h4>
                      <span className="dm-column-count">{tables[selectedTable].column_count || 0} columns</span>
                    </div>
                    {tables[selectedTable].columns ? (
                      <div className="dm-column-list">
                        {tables[selectedTable].columns.map((col, j) => (
                          <div key={j} className="dm-column-item">
                            <span className="dm-col-name">{col.name}</span>
                            <span className="dm-col-type">{col.type}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="dm-empty">Column details not available</p>
                    )}
                    <div className="dm-table-dates">
                      {tables[selectedTable].created_at && (
                        <span>Created: {tables[selectedTable].created_at.split('T')[0]}</span>
                      )}
                      {tables[selectedTable].source_file && (
                        <span>Source: {tables[selectedTable].source_file}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
